import React, { MutableRefObject, useRef } from 'react';
import { createPortal } from 'react-dom';
import './DropDownOverlay.less';
import { Popper } from 'react-popper';
import { Behavior, Placement, Position } from 'popper.js';
import { useOnClickOutside } from 'hooks/useOnOutsideClick';
import { CalloutContent } from 'components/layout/CalloutContent';
import classNames from 'classnames';
import { CalloutCaret, CALLOUT_CARET_SIZE } from 'components/layout/CalloutCaret';
import { useMaxZIndex } from 'hooks/useMaxZIndex';
import { useDidUpdateEffect } from 'hooks/useDidUpdateEffect';

/**
 * Props for rendering an overlay
 * Compare MVC.Host popuplayer.ts
 */
export interface IOverlayProps {
  id?: string;
  isOpen: boolean;
  sourceRef: MutableRefObject<HTMLElement | null>;
  onClose: (event: MouseEvent | TouchEvent) => void;
  children: React.ReactNode;
  className?: string;
  /**
   * TODO: implement
   * Sets the callout's height
   */
  callOutHeight?: number | string;
  callOutMinHeight?: number | string;
  /**
   * Sets the callout's maximum width.
   * @default 400px
   */
  callOutMaxHeight?: number | string;
  /**
   * Sets the callout's width.
   * By default width will be equaled "sourceRef.width".
   */
  callOutWidth?: number | string;
  callOutMinWidth?: number | string;
  callOutMaxWidth?: number | string;
  /**
   * Sets placement for callout.
   * @default "bottom-start"
   */
  placement?: Placement;
  'data-testid'?: string;
  /** If you need to specify where the callout goes when there's no */
  flipBehavior?: Behavior | Position[];
  /**
   * How many callouts-deep is this one. Only the first level will
   * be rendered in a portal at the document root.
   */
  depth?: number;
  tabIndex?: number;
  offset?: number | string;
  showArrow?: boolean;
}

const getClassName = (placement: Placement, className?: string, showArrow?: boolean) =>
  classNames(className, 'dpc-drop-down', 'ui-front', {
    'dpc-drop-down--up': placement === 'top-start',
    'dpc-callout': showArrow,
  });

/**
 * Rendering an overlay, with logic in one place, to avoid complication from potential duplicates.
 *
 * TODO alex: come up with new component name as we do not render overlay anymore
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export function DropDownOverlay({
  id,
  children,
  className,
  isOpen,
  onClose,
  sourceRef,
  callOutHeight,
  callOutMinHeight,
  callOutMaxHeight,
  callOutWidth,
  callOutMinWidth,
  callOutMaxWidth,
  placement,
  flipBehavior,
  tabIndex,
  offset,
  showArrow,
  depth,
  ...otherProps
}: IOverlayProps): JSX.Element | React.ReactPortal | null {
  const layerRef = useRef<HTMLElement>(null);
  useOnClickOutside([layerRef, sourceRef], onClose);

  const { zIndex, refresh } = useMaxZIndex();

  useDidUpdateEffect(() => {
    if (isOpen && sourceRef.current) {
      refresh();
    }
  }, [isOpen, refresh, sourceRef]);

  if (!isOpen || !sourceRef.current) {
    // don't render if not open, obviously
    // don't render if the source ref isn't populated, because we don't know where, yet.
    return null;
  }

  const content = (
    <Popper
      innerRef={layerRef}
      placement={placement ?? 'bottom-start'}
      eventsEnabled={true}
      referenceElement={sourceRef.current}
      modifiers={{
        flip: {
          behavior: flipBehavior ?? 'flip',
        },
        preventOverflow: {
          boundariesElement: 'window',
        },
        offset: showArrow
          ? {
              enabled: showArrow,
              // "length, width" relative to the "placement" orientation https://popper.js.org/docs/v1/#modifiers..offset
              offset: `0, ${CALLOUT_CARET_SIZE * 0.8}px`,
            }
          : offset
          ? {
              offset,
            }
          : undefined,
      }}
    >
      {({ ref, style, placement, scheduleUpdate, arrowProps }) => {
        return isOpen ? (
          <CalloutContent
            id={id}
            placement={placement}
            className={getClassName(placement, className, showArrow)}
            style={getDropDownPos(sourceRef.current!, style, zIndex, {
              height: callOutHeight,
              minHeight: callOutMinHeight,
              maxHeight: callOutMaxHeight,
              width: callOutWidth,
              minWidth: callOutMinWidth,
              maxWidth: callOutMaxWidth,
            })}
            tabIndex={tabIndex}
            scheduleUpdate={scheduleUpdate}
            ref={ref}
            data-testid={otherProps['data-testid']}
          >
            {showArrow && (
              <CalloutCaret
                {...arrowProps}
                style={{
                  ...arrowProps.style,
                  width: `${CALLOUT_CARET_SIZE}px`,
                }}
                placement={placement}
              />
            )}
            {children}
          </CalloutContent>
        ) : null;
      }}
    </Popper>
  );

  return depth ? content : createPortal(content, document.body);
}

function getDropDownPos(
  sourceRef: HTMLElement,
  style: React.CSSProperties,
  zIndex: number,
  size: {
    height?: number | string;
    minHeight?: number | string;
    maxHeight?: number | string;
    width?: number | string;
    minWidth?: number | string;
    maxWidth?: number | string;
  }
): React.CSSProperties {
  const rect = sourceRef.getBoundingClientRect();
  return {
    ...style,
    height: size.height,
    minHeight: size.minHeight,
    maxHeight: size.maxHeight ?? '400px',
    width: size?.width ?? `${rect.width}px`,
    maxWidth: size?.maxWidth,
    minWidth: size?.minWidth,
    zIndex,
  };
}
